import { useCallback, useEffect, useRef, useState } from "react";

import { ScaleAnalysesHttpService } from "./scale-analyses-http.service";

interface ScaleTabEligibility {
  isEligible: boolean;
  isLoading: boolean;
}

// The SCALES tab is shown only for tests that belong to an assessment template. `isLoading` lets the
// caller hold off rendering the tab strip until eligibility is known, so the tab never flashes.
export function useScaleTabEligibility(testId: string | number | undefined): ScaleTabEligibility {
  const [isEligible, setIsEligible] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  // This request outlives a quick navigation away from the test page; React 17 warns on the state
  // update that would follow.
  const isMounted = useRef(true);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  const fetchEligibility = useCallback(async () => {
    if (!testId) {
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      const { belongsToAssessmentTemplate } = await ScaleAnalysesHttpService.getEligibility(testId);
      if (isMounted.current) {
        setIsEligible(belongsToAssessmentTemplate);
      }
    } catch {
      // Eligibility only decides whether an extra tab appears, so a failure hides it rather than
      // interrupting the rest of the test-detail page with an error.
      if (isMounted.current) {
        setIsEligible(false);
      }
    } finally {
      if (isMounted.current) {
        setIsLoading(false);
      }
    }
  }, [testId]);

  useEffect(() => {
    fetchEligibility();
  }, [fetchEligibility]);

  return { isEligible, isLoading };
}
